import React, { useCallback } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import catchOnly from 'utils/catchOnly';

import useEnhancedCheckout, {
  EnhancedCheckoutError,
} from 'hooks/requests/enhancedCheckout';
import { OTPSenderError } from 'hooks/requests/otpSender';
import { NonClientError } from 'hooks/requests/fetchUtils';
import useQueryParameters from 'hooks/useQueryParameters';
import useNavigationHelpers from 'hooks/useNavigationHelpers';
import ReusableConsumerSearch from 'features/ReusableConsumerSearch';
import useAnalytics, {
  Event,
  useTrackApplicationStart,
} from 'hooks/useAnalytics';

export interface FormValues {
  mobilePhone: string | null;
  ssn: string;
  zip: string;
}

type PropTypes = RouteComponentProps;

const ConsumerSearch: React.FC<PropTypes> = props => {
  useTrackApplicationStart(undefined, Event.EnhancedCheckoutStarted);
  const [enhancedCheckout] = useEnhancedCheckout();

  const { push } = useNavigationHelpers();
  const { enqueueSnackbar } = useSnackbar();

  const query = useQueryParameters();

  const errorPath = '/enhanced-checkout/error';

  const { sendEvents } = useAnalytics();

  const onSubmit = useCallback(
    async (values: {
      ssn: string;
      zip: string;
      mobilePhone?: string;
      otpCode?: string;
    }) => {
      try {
        const response = await enhancedCheckout({
          mobilePhone: values.mobilePhone,
          ssn: values.ssn,
          zip: values.zip,
          carrier: query.carrier,
          keyword: query.keyword,
          shortCode: query.shortCode,
          token: query.token,
          otpCode: values.otpCode,
        });

        if (response.hasExistingAccount) {
          sendEvents([Event.EnhancedCheckoutSuccessful]);
          if (response.existingAccounts.length > 1) {
            push('/account-selection');
          } else {
            push('/approval');
          }
        } else {
          sendEvents([Event.EnhancedCheckoutNotFound]);
          push(errorPath);
        }
      } catch (e) {
        sendEvents([Event.EnhancedCheckoutError]);
        if (
          catchOnly(e, NonClientError, EnhancedCheckoutError, OTPSenderError)
        ) {
          if (
            e instanceof NonClientError ||
            e instanceof EnhancedCheckoutError
          ) {
            push(errorPath);
          } else {
            enqueueSnackbar(
              'Something has gone wrong. Please try again later.',
              {
                variant: 'error',
                persist: true,
              },
            );
          }
        }
      }
    },
    [enhancedCheckout, enqueueSnackbar, push, query, errorPath, sendEvents],
  );

  return (
    <ReusableConsumerSearch
      isAccountLookup
      lookupType={query.token ? 'tokenLookup' : 'phoneLookup'}
      onSubmit={onSubmit}
      hasExistingAccount={query.hasExistingAccount}
      i18nPrefix="enhancedCheckout.consumerSearch"
      {...props}
    />
  );
};

export default ConsumerSearch;
